import React, { useEffect, useMemo, useState } from "react";
import { Badge, Button, Icon, List, Popover, Spin, Tooltip, Typography } from "antd";
import { connect } from "react-redux";
import moment from "moment";
import Api from "../../helpers/apiHandler";
import { socket } from "../../socket";
import variablesAction from "../../redux/variables/actions";
import { withRouter } from "react-router-dom";

const api = new Api();
const { Text } = Typography;
const { setSocket } = variablesAction;

const coerceMoment = (createdAt) => {
  if (!createdAt) return null;
  if (typeof createdAt === "number") return moment(createdAt);
  return moment(createdAt);
};

const getNavigationPath = (n) => {
  const refType = (n && n.referenceType) || "";
  const refId = n && n.referenceId;
  if (!refId) return null;

  if (refType === "equipmentAsset") {
    return `/assets/view/${refId}`;
  }
  if (refType === "equipmentWorkOrder") {
    return `/assets/work-orders?workOrderId=${refId}`;
  }
  if (refType === "equipmentRepeatingWorkOrder") {
    return `/assets/repeating-work-orders?templateId=${refId}`;
  }

  if (refType === "businessLocation") return `/company/productionUnitInfo/${refId}`;
  if (refType === "documentControlDocument") return "/document-control";

  // Best-effort fallbacks. We don't currently have businessLocationId in these notifications.
  if (refType === "businessLocationCertification") return "/company";
  if (refType === "businessLocationRegulatory") return "/company";
  if (refType === "businessLocationRoleAssignment") return "/company";

  return null;
};

const formatTime = (created) => {
  if (!created) return "";
  const m = moment(created);
  if (m.isSame(moment(), "day")) return m.format("HH:mm");
  if (m.isSame(moment(), "year")) return m.format("MMM D");
  return m.format("YYYY-MM-DD");
};

const typeTagColor = (type) => {
  switch (type) {
    case "CERTIFICATION_EXPIRED":
    case "REGULATORY_IDENTIFIER_EXPIRED":
    case "EQUIPMENT_WORK_ORDER_PAST_DUE":
    case "EQUIPMENT_CHECKOUT_OVERDUE":
    case "EQUIPMENT_EXTERNAL_WO_NOT_ACKNOWLEDGED":
      return "red";
    case "CERTIFICATION_EXPIRING_SOON":
    case "EQUIPMENT_WARRANTY_EXPIRING":
    case "EQUIPMENT_CALIBRATION_DUE":
    case "EQUIPMENT_MAINTENANCE_DUE":
    case "EQUIPMENT_USAGE_THRESHOLD_REACHED":
      return "orange";
    case "BUSINESS_LOCATION_CREATED":
    case "EQUIPMENT_WORK_ORDER_ASSIGNED":
    case "EQUIPMENT_EXTERNAL_WO_ACKNOWLEDGED":
      return "blue";
    default:
      return "default";
  }
};

const typeDotColor = (type) => {
  switch (type) {
    case "CERTIFICATION_EXPIRED":
    case "REGULATORY_IDENTIFIER_EXPIRED":
    case "EQUIPMENT_WORK_ORDER_PAST_DUE":
    case "EQUIPMENT_CHECKOUT_OVERDUE":
    case "EQUIPMENT_EXTERNAL_WO_NOT_ACKNOWLEDGED":
      return "#ff4d4f";
    case "CERTIFICATION_EXPIRING_SOON":
    case "EQUIPMENT_WARRANTY_EXPIRING":
    case "EQUIPMENT_CALIBRATION_DUE":
    case "EQUIPMENT_MAINTENANCE_DUE":
    case "EQUIPMENT_USAGE_THRESHOLD_REACHED":
      return "#faad14";
    case "BUSINESS_LOCATION_CREATED":
    case "EQUIPMENT_WORK_ORDER_ASSIGNED":
    case "EQUIPMENT_EXTERNAL_WO_ACKNOWLEDGED":
      return "#1890ff";
    default:
      return "#d9d9d9";
  }
};

const AppNotificationBadge = ({ socketData, companyId, userId, history, setSocket }) => {
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [appUnreadCount, setAppUnreadCount] = useState(0);
  const [socketUnreadCount, setSocketUnreadCount] = useState(0);
  const [lastNotification, setLastNotification] = useState(null);
  const [rows, setRows] = useState([]);

  const totalUnreadCount = appUnreadCount + socketUnreadCount;

  const effectiveCompanyId = useMemo(() => Number(companyId) || 0, [companyId]);

  // Ensure socket exists so notifications can be instant on any page.
  useEffect(() => {
    if (!userId) return;
    const hasSocket = socketData && typeof socketData.on === "function";
    if (!hasSocket) {
      const socketInstance = socket(userId);
      setSocket(socketInstance);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const fetchUnreadCount = async () => {
    if (!effectiveCompanyId) return;
    const res = await api.get("common/notifications/unread-count", {
      params: { companyId: effectiveCompanyId },
    });
    if (res && res.status === 1 && res.data) {
      setAppUnreadCount(Number(res.data.unreadCount) || 0);
    }
  };

  const fetchList = async () => {
    if (!effectiveCompanyId) return;
    setLoading(true);
    try {
      const res = await api.get("common/notifications/list", {
        params: { companyId: effectiveCompanyId, page: 0, limit: 50 },
      });
      if (res && res.status === 1 && res.data) {
        setRows(res.data.rows || []);
        setAppUnreadCount(Number(res.data.unreadCount) || 0);
      }
    } finally {
      setLoading(false);
    }
  };

  const markRead = async (id) => {
    if (!id) return;
    // optimistic UI update
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, isRead: true } : r)));
    setAppUnreadCount((prev) => Math.max(0, prev - 1));
    const res = await api.put(`common/notifications/read/${id}`, {
      isMultipart: false,
      data: {},
    });
    if (res && res.status === 1) {
      return;
    }
  };

  const markAllRead = async () => {
    if (!effectiveCompanyId) return;
    const res = await api.put("common/notifications/read-all", {
      isMultipart: false,
      data: {},
      params: { companyId: effectiveCompanyId },
    });
    if (res && res.status === 1) {
      setRows((prev) => prev.map((r) => ({ ...r, isRead: true })));
      setAppUnreadCount(0);
    }
  };

  useEffect(() => {
    fetchUnreadCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveCompanyId]);

  useEffect(() => {
    if (!socketData || typeof socketData.on !== "function") return;
    const handleAppNotification = (data) => {
      if (!data) return;
      setRows((prev) => [data, ...(prev || [])].slice(0, 50));
      setAppUnreadCount((prev) => prev + 1);
    };
    socketData.on("app_notification", handleAppNotification);
    return () => {
      if (socketData && typeof socketData.off === "function") {
        socketData.off("app_notification", handleAppNotification);
      }
    };
  }, [socketData]);

  useEffect(() => {
    if (!socketData || !socketData.connected) return;

    const handleTaskNotification = (data) => {
      setSocketUnreadCount((prev) => prev + 1);
      if (data && data.taskId) {
        setLastNotification(data);
      }
    };

    const handleMessageReceive = (data) => {
      if (!data.isTaskMessage) {
        setSocketUnreadCount((prev) => prev + 1);
      }
    };

    socketData.on("task_notification", handleTaskNotification);
    socketData.on("message/receive", handleMessageReceive);

    return () => {
      if (socketData && socketData.connected) {
        socketData.off("task_notification", handleTaskNotification);
        socketData.off("message/receive", handleMessageReceive);
      }
    };
  }, [socketData]);

  const handleBellClick = (e) => {
    if (lastNotification && lastNotification.taskId) {
      e.stopPropagation();
      history.push(`/task/view/${lastNotification.taskId}`);
      setSocketUnreadCount(0);
      setLastNotification(null);
    }
  };

  const onNotificationClick = async (item) => {
    if (!item) return;
    if (!item.isRead) {
      // don't block navigation on network
      markRead(item.id);
    }
    const target = getNavigationPath(item);
    setVisible(false);
    if (target) {
      history.push(target);
    }
  };

  const content = (
    <div style={{ width: 360, overflowX: "hidden" }} onClick={(e) => e.stopPropagation()}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Text strong>System Notifications</Text>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Button size="small" onClick={markAllRead} disabled={!appUnreadCount}>
            Mark all read
          </Button>
          <Button size="small" onClick={fetchList} title="Refresh">
            <Icon type="reload" />
          </Button>
        </div>
      </div>
      {loading ? (
        <div style={{ padding: 16, textAlign: "center" }}>
          <Spin />
        </div>
      ) : (
        <div style={{ maxHeight: 420, overflowY: "auto", overflowX: "hidden" }}>
          <List
            dataSource={rows || []}
            locale={{ emptyText: "No notifications" }}
            renderItem={(item) => {
              const created = coerceMoment(item.createdAt);
              const timeText = formatTime(created);
              return (
                <List.Item
                  style={{
                    cursor: "pointer",
                    opacity: item.isRead ? 0.75 : 1,
                    padding: "10px 8px",
                    borderBottom: "1px solid #f0f0f0",
                    overflow: "hidden",
                  }}
                  onClick={() => onNotificationClick(item)}
                >
                  <div style={{ width: "100%" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: 99,
                          background: item.isRead ? "#d9d9d9" : typeDotColor(item.type),
                          flex: "0 0 auto",
                        }}
                      />
                      <Text strong style={{ flex: 1, minWidth: 0 }} ellipsis>
                        {item.title || "Notification"}
                      </Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {timeText}
                      </Text>
                    </div>
                    <div style={{ marginTop: 4 }}>
                      <Tooltip title={item.body || ""} placement="topLeft">
                        <Text type="secondary" ellipsis={{ rows: 2 }}>
                          {item.body || ""}
                        </Text>
                      </Tooltip>
                    </div>
                  </div>
                </List.Item>
              );
            }}
          />
        </div>
      )}
    </div>
  );

  const badgeCount =
    totalUnreadCount > 99 ? "99+" : totalUnreadCount > 0 ? totalUnreadCount : 0;

  return (
    <div onClick={(e) => e.stopPropagation()} className="app-notification-badge">
      <style>
        {`
          .app-notification-badge .ant-badge {
            font-size: 0;
            line-height: 1;
          }
          .app-notification-badge .ant-badge-count {
            min-width: 14px;
            height: 14px;
            padding: 0 3px;
            font-size: 10px;
            line-height: 14px;
            border-radius: 7px;
          }
          .app-notification-badge .ant-badge-multiple-words {
            min-width: 18px;
            padding: 0 4px;
          }
          .app-notification-badge__bell {
            font-size: 18px;
            color: #fff;
            line-height: 1;
            display: inline-block;
          }
        `}
      </style>
      <Popover
        content={content}
        trigger="click"
        placement="topLeft"
        visible={visible}
        onVisibleChange={(v) => {
          setVisible(v);
          if (v) fetchList();
        }}
      >
        <Tooltip title="System Notifications" placement="top">
          <span
            style={{
              position: "relative",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
            }}
            onClick={handleBellClick}
          >
            <Badge
              count={badgeCount}
              overflowCount={99}
              offset={[4, -3]}
              title={
                totalUnreadCount > 0
                  ? `${totalUnreadCount} unread notification${totalUnreadCount === 1 ? "" : "s"}`
                  : ""
              }
            >
              <Icon type="bell" className="app-notification-badge__bell" />
            </Badge>
          </span>
        </Tooltip>
      </Popover>
    </div>
  );
};

export default withRouter(
  connect(
    (state) => ({
      companyId: state.Auth && state.Auth.company ? state.Auth.company.id : 0,
      userId: state.Auth ? state.Auth.id : 0,
      socketData: state.Generals.socketData,
    }),
    { setSocket }
  )(AppNotificationBadge)
);
